/**
 * Plaid integration type definitions
 * Comprehensive types matching the backend implementation
 */

import { Account } from './index';

// ==========================================
// Core Plaid Types
// ==========================================

/**
 * Plaid environment configuration
 */
export type PlaidEnvironment = 'sandbox' | 'development' | 'production';

/**
 * Plaid connection status
 */
export enum PlaidConnectionStatus {
  ACTIVE = 'ACTIVE',
  NEEDS_REAUTH = 'NEEDS_REAUTH',
  DISCONNECTED = 'DISCONNECTED',
  ERROR = 'ERROR'
}

/**
 * Main Plaid connection entity
 */
export interface PlaidConnection {
  organizationId: string;
  id: string;  // Same as itemId from Plaid
  itemId: string;  // Plaid's unique identifier
  accessToken?: string;  // Encrypted, not exposed to frontend
  institutionId: string;
  institutionName: string;
  institutionLogo?: string;
  lastSyncCursor?: string;
  lastSyncTime?: Date | string;
  lastTransactionDate?: Date | string;
  accountsData: PlaidAccountData[];
  isActive: boolean;
  needsReauth: boolean;
  createdAt: Date | string;
  updatedAt: Date | string;
  status?: PlaidConnectionStatus;
  error?: string;
}

/**
 * Plaid account data stored with connection
 */
export interface PlaidAccountData {
  account_id: string;
  name: string;
  type: PlaidAccountType;
  subtype?: PlaidAccountSubtype;
  mask?: string;
  official_name?: string;
}

/**
 * Full Plaid account entity
 */
export interface PlaidAccount {
  organizationId: string;
  id: string;  // Same as account_id from Plaid
  connectionId: string;  // References PlaidConnection.id
  accountId: string;  // Plaid's account_id
  name: string;
  officialName?: string;
  type: PlaidAccountType;
  subtype?: PlaidAccountSubtype;
  mask?: string;
  currentBalance?: number;
  availableBalance?: number;
  isoCurrencyCode?: string;
  unofficialCurrencyCode?: string;
  lastUpdated: Date | string;
  createdAt: Date | string;
  updatedAt: Date | string;
}

/**
 * Plaid account types
 */
export enum PlaidAccountType {
  INVESTMENT = 'investment',
  CREDIT = 'credit',
  DEPOSITORY = 'depository',
  LOAN = 'loan',
  OTHER = 'other'
}

/**
 * Plaid account subtypes
 */
export enum PlaidAccountSubtype {
  // Depository subtypes
  CHECKING = 'checking',
  SAVINGS = 'savings',
  HSA = 'hsa',
  CD = 'cd',
  MONEY_MARKET = 'money market',
  PAYPAL = 'paypal',
  PREPAID = 'prepaid',
  CASH_MANAGEMENT = 'cash management',
  EBT = 'ebt',
  
  // Credit subtypes
  CREDIT_CARD = 'credit card',
  
  // Loan subtypes
  AUTO = 'auto',
  BUSINESS = 'business',
  COMMERCIAL = 'commercial',
  CONSTRUCTION = 'construction',
  CONSUMER = 'consumer',
  HOME_EQUITY = 'home equity',
  LINE_OF_CREDIT = 'line of credit',
  MORTGAGE = 'mortgage',
  OVERDRAFT = 'overdraft',
  STUDENT = 'student',
  
  // Investment subtypes
  INVESTMENT_401A = '401a',
  INVESTMENT_401K = '401k',
  INVESTMENT_403B = '403b',
  INVESTMENT_457B = '457b',
  INVESTMENT_529 = '529',
  BROKERAGE = 'brokerage',
  CASH_ISA = 'cash isa',
  EDUCATION_SAVINGS_ACCOUNT = 'education savings account',
  FIXED_ANNUITY = 'fixed annuity',
  GIC = 'gic',
  HEALTH_REIMBURSEMENT_ARRANGEMENT = 'health reimbursement arrangement',
  IRA = 'ira',
  ISA = 'isa',
  KEOGH = 'keogh',
  LIF = 'lif',
  LIRA = 'lira',
  LRIF = 'lrif',
  LRSP = 'lrsp',
  MUTUAL_FUND = 'mutual fund',
  NON_TAXABLE_BROKERAGE = 'non-taxable brokerage',
  PENSION = 'pension',
  PLAN = 'plan',
  PRIF = 'prif',
  PROFIT_SHARING_PLAN = 'profit sharing plan',
  RDSP = 'rdsp',
  RESP = 'resp',
  RETIREMENT = 'retirement',
  RLIF = 'rlif',
  ROTH = 'roth',
  ROTH_401K = 'roth 401k',
  RRIF = 'rrif',
  RRSP = 'rrsp',
  SARSEP = 'sarsep',
  SEP_IRA = 'sep ira',
  SIMPLE_IRA = 'simple ira',
  SIPP = 'sipp',
  STOCK_PLAN = 'stock plan',
  TFSA = 'tfsa',
  TRUST = 'trust',
  UGMA = 'ugma',
  UTMA = 'utma',
  VARIABLE_ANNUITY = 'variable annuity'
}

/**
 * Plaid transaction entity
 */
export interface PlaidTransaction {
  transaction_id: string;
  account_id: string;
  amount: number;
  iso_currency_code?: string;
  unofficial_currency_code?: string;
  category?: string[];
  category_id?: string;
  date: string;  // YYYY-MM-DD
  datetime?: string;  // ISO 8601
  authorized_date?: string;
  authorized_datetime?: string;
  name: string;
  merchant_name?: string;
  payment_channel: PlaidPaymentChannel;
  pending: boolean;
  pending_transaction_id?: string;
  transaction_type?: PlaidTransactionType;
  transaction_code?: string;
  personal_finance_category?: PlaidPersonalFinanceCategory;
}

/**
 * Plaid payment channels
 */
export enum PlaidPaymentChannel {
  ONLINE = 'online',
  IN_STORE = 'in store',
  OTHER = 'other'
}

/**
 * Plaid transaction types
 */
export enum PlaidTransactionType {
  DIGITAL = 'digital',
  PLACE = 'place',
  SPECIAL = 'special',
  UNRESOLVED = 'unresolved'
}

/**
 * Plaid personal finance category
 */
export interface PlaidPersonalFinanceCategory {
  primary: string;
  detailed: string;
}

/**
 * Transaction sync tracking
 */
export interface PlaidTransactionSync {
  organizationId: string;
  connectionId: string;
  transactionId: string;
  status: 'ADDED' | 'MODIFIED' | 'REMOVED';
  syncedAt: Date | string;
  webhookId?: string;
}

// ==========================================
// API Request/Response Types
// ==========================================

/**
 * Create link token request
 */
export interface CreateLinkTokenRequest {
  // No parameters needed - organizationId from auth context
}

/**
 * Create link token response
 */
export interface CreateLinkTokenResponse {
  linkToken: string;
  expiration: string;
}

/**
 * Exchange public token request
 */
export interface ExchangePublicTokenRequest {
  publicToken: string;
  metadata?: PlaidLinkMetadata;
}

/**
 * Exchange public token response
 */
export interface ExchangePublicTokenResponse {
  success: boolean;
  itemId: string;
  message?: string;
}

/**
 * Get connections request
 */
export interface GetConnectionsRequest {
  includeInactive?: boolean;
}

/**
 * Get connections response
 */
export interface GetConnectionsResponse {
  connections: PlaidConnection[];
}

/**
 * Sync transactions request
 */
export interface SyncTransactionsRequest {
  connectionId: string;  // Required - sync specific connection
}

/**
 * Sync transactions response (reuses bulk import result)
 */
export interface SyncTransactionsResponse {
  autoApproved: number;
  needsReview: number;
  failed: number;
  errors: string[];
  message?: string;
  modified?: number;
  removed?: number;
  finStackAccounts?: Account[]; // Updated FinStack accounts with new balances
}

/**
 * Get account balances request
 */
export interface GetAccountBalancesRequest {
  connectionId?: string;
}

/**
 * Account balance from Plaid
 */
export interface PlaidAccountBalance {
  accountId: string;
  accountName: string;
  connectionId: string;
  institutionName: string;
  current: number;
  available?: number;
  limit?: number;
  currency: string;
  lastUpdated: Date | string;
  type: PlaidAccountType;
  subtype?: PlaidAccountSubtype;
}

/**
 * Get account balances response
 */
export interface GetAccountBalancesResponse {
  balances: PlaidAccountBalance[];
  totalBalance: number;
  currency: string;
}

/**
 * Disconnect account request
 */
export interface DisconnectAccountRequest {
  itemId: string;
  reason?: string;
}

/**
 * Disconnect account response
 */
export interface DisconnectAccountResponse {
  success: boolean;
  message?: string;
}

/**
 * Get connection status request
 */
export interface GetConnectionStatusRequest {
  connectionId: string;
}

/**
 * Connection status details
 */
export interface ConnectionStatusDetails {
  connectionId: string;
  status: PlaidConnectionStatus;
  lastSync?: Date | string;
  transactionCount?: number;
  accountCount: number;
  error?: PlaidError;
  needsReauth: boolean;
}

/**
 * Get connection status response
 */
export interface GetConnectionStatusResponse {
  status: ConnectionStatusDetails;
}

/**
 * Bulk sync request
 */
export interface BulkSyncTransactionsRequest {
  connectionIds?: string[];  // Optional - sync specific connections
  forceFullSync?: boolean;
}

/**
 * Bulk sync response
 */
export interface BulkSyncTransactionsResponse {
  results: Array<{
    connectionId: string;
    institutionName: string;
    success: boolean;
    transactionsAdded?: number;
    transactionsModified?: number;
    transactionsRemoved?: number;
    error?: string;
  }>;
  totalAdded: number;
  totalModified: number;
  totalRemoved: number;
  errors: string[];
}

/**
 * Test connection request
 */
export interface TestConnectionRequest {
  // No parameters needed
}

/**
 * Test connection response
 */
export interface TestConnectionResponse {
  serviceAvailable: boolean;
  environment: PlaidEnvironment;
  message: string;
}

/**
 * Get analytics request
 */
export interface GetAnalyticsRequest {
  connectionId?: string;
  startDate?: string;  // YYYY-MM-DD
  endDate?: string;    // YYYY-MM-DD
}

/**
 * Connection analytics
 */
export interface PlaidAnalytics {
  organizationId: string;
  connections: {
    total: number;
    active: number;
    needsReauth: number;
    byInstitution: Array<{
      institutionName: string;
      count: number;
    }>;
  };
  accounts: {
    total: number;
    byType: Record<PlaidAccountType, number>;
  };
  transactions: {
    total: number;
    lastSyncDate?: Date | string;
    last30Days: number;
    byStatus: {
      approved: number;
      underReview: number;
      declined: number;
    };
  };
  syncHistory: Array<{
    date: Date | string;
    connectionsSync: number;
    transactionsAdded: number;
    transactionsModified: number;
    transactionsRemoved: number;
  }>;
}

/**
 * Get analytics response
 */
export interface GetAnalyticsResponse {
  analytics: PlaidAnalytics;
}

// ==========================================
// Plaid Link Types
// ==========================================

/**
 * Plaid Link metadata from onSuccess callback
 */
export interface PlaidLinkMetadata {
  institution?: {
    name: string;
    institution_id: string;
  };
  accounts: Array<{
    id: string;
    name: string;
    type: string;
    subtype?: string;
    mask?: string;
  }>;
  link_session_id: string;
  public_token: string;
}

/**
 * Plaid Link onExit callback metadata
 */
export interface PlaidLinkOnExitMetadata {
  error?: PlaidError;
  institution?: {
    name: string;
    institution_id: string;
  };
  link_session_id: string;
  request_id: string;
  status?: string;
}

// ==========================================
// Error Types
// ==========================================

/**
 * Plaid error structure
 */
export interface PlaidError {
  error_type: string;
  error_code: string;
  error_message: string;
  display_message?: string;
  causes?: string[];
  status?: number;
  documentation_url?: string;
  suggested_action?: string;
}

/**
 * Common Plaid error codes
 */
export enum PlaidErrorCode {
  // Item errors
  ITEM_LOGIN_REQUIRED = 'ITEM_LOGIN_REQUIRED',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  ITEM_LOCKED = 'ITEM_LOCKED',
  USER_SETUP_REQUIRED = 'USER_SETUP_REQUIRED',
  MFA_REQUIRED = 'MFA_REQUIRED',
  INSUFFICIENT_CREDENTIALS = 'INSUFFICIENT_CREDENTIALS',
  
  // Institution errors
  INSTITUTION_DOWN = 'INSTITUTION_DOWN',
  INSTITUTION_NOT_RESPONDING = 'INSTITUTION_NOT_RESPONDING',
  INSTITUTION_NOT_SUPPORTED = 'INSTITUTION_NOT_SUPPORTED',
  
  // API errors
  INVALID_REQUEST = 'INVALID_REQUEST',
  INVALID_INPUT = 'INVALID_INPUT',
  RATE_LIMIT_EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  API_ERROR = 'API_ERROR',
  
  // Product errors
  PRODUCT_NOT_READY = 'PRODUCT_NOT_READY',
  PRODUCTS_NOT_SUPPORTED = 'PRODUCTS_NOT_SUPPORTED'
}

// ==========================================
// Webhook Types
// ==========================================

/**
 * Plaid webhook types
 */
export enum PlaidWebhookType {
  TRANSACTIONS = 'TRANSACTIONS',
  ITEM = 'ITEM',
  AUTH = 'AUTH',
  IDENTITY = 'IDENTITY',
  ASSETS = 'ASSETS',
  HOLDINGS = 'HOLDINGS',
  INVESTMENTS_TRANSACTIONS = 'INVESTMENTS_TRANSACTIONS',
  LIABILITIES = 'LIABILITIES',
  PAYMENT_INITIATION = 'PAYMENT_INITIATION'
}

/**
 * Plaid webhook codes
 */
export enum PlaidWebhookCode {
  // Transactions webhooks
  INITIAL_UPDATE = 'INITIAL_UPDATE',
  HISTORICAL_UPDATE = 'HISTORICAL_UPDATE',
  DEFAULT_UPDATE = 'DEFAULT_UPDATE',
  TRANSACTIONS_REMOVED = 'TRANSACTIONS_REMOVED',
  
  // Item webhooks
  ERROR = 'ERROR',
  NEW_ACCOUNTS_AVAILABLE = 'NEW_ACCOUNTS_AVAILABLE',
  PENDING_EXPIRATION = 'PENDING_EXPIRATION',
  USER_PERMISSION_REVOKED = 'USER_PERMISSION_REVOKED',
  WEBHOOK_UPDATE_ACKNOWLEDGED = 'WEBHOOK_UPDATE_ACKNOWLEDGED'
}

/**
 * Plaid webhook payload
 */
export interface PlaidWebhook {
  webhook_type: PlaidWebhookType;
  webhook_code: PlaidWebhookCode;
  item_id: string;
  error?: PlaidError;
  new_transactions?: number;
  removed_transactions?: string[];
}

// ==========================================
// UI State Types
// ==========================================

/**
 * Plaid Link state for UI components
 */
export interface PlaidLinkState {
  isLoading: boolean;
  isReady: boolean;
  error?: string;
  linkToken?: string;
}

/**
 * Connection management state
 */
export interface PlaidConnectionsState {
  connections: PlaidConnection[];
  isLoading: boolean;
  error?: string;
  selectedConnection?: PlaidConnection;
}

/**
 * Sync state for transaction syncing
 */
export interface PlaidSyncState {
  isSyncing: boolean;
  progress?: {
    current: number;
    total: number;
    message: string;
  };
  lastSync?: Date | string;
  result?: SyncTransactionsResponse;
  error?: string;
}

// ==========================================
// Utility Types
// ==========================================

/**
 * Plaid date formats
 */
export type PlaidDate = string;  // YYYY-MM-DD
export type PlaidDateTime = string;  // ISO 8601

/**
 * Currency codes used by Plaid
 */
export type PlaidCurrencyCode = string;  // ISO 4217 code

/**
 * Plaid amount (always positive for UI display)
 */
export type PlaidAmount = number;

/**
 * Type guards
 */
export const PlaidTypeGuards = {
  isPlaidError: (error: any): error is PlaidError => {
    return error && 
      typeof error.error_type === 'string' &&
      typeof error.error_code === 'string' &&
      typeof error.error_message === 'string';
  },
  
  isNeedsReauth: (connection: PlaidConnection): boolean => {
    return connection.needsReauth || connection.status === PlaidConnectionStatus.NEEDS_REAUTH;
  },
  
  isActiveConnection: (connection: PlaidConnection): boolean => {
    return connection.isActive && !connection.needsReauth;
  },
  
  isCreditAccount: (account: PlaidAccount | PlaidAccountData): boolean => {
    return account.type === PlaidAccountType.CREDIT;
  },
  
  isDepositoryAccount: (account: PlaidAccount | PlaidAccountData): boolean => {
    return account.type === PlaidAccountType.DEPOSITORY;
  }
};

/**
 * Plaid utilities
 */
export const PlaidUtils = {
  /**
   * Format Plaid error for display
   */
  formatError: (error: PlaidError): string => {
    return error.display_message || error.error_message || 'An error occurred';
  },
  
  /**
   * Get account type label
   */
  getAccountTypeLabel: (type: PlaidAccountType): string => {
    switch (type) {
      case PlaidAccountType.DEPOSITORY:
        return 'Bank Account';
      case PlaidAccountType.CREDIT:
        return 'Credit Card';
      case PlaidAccountType.INVESTMENT:
        return 'Investment';
      case PlaidAccountType.LOAN:
        return 'Loan';
      default:
        return 'Other';
    }
  },
  
  /**
   * Get connection status label
   */
  getConnectionStatusLabel: (status: PlaidConnectionStatus): string => {
    switch (status) {
      case PlaidConnectionStatus.ACTIVE:
        return 'Active';
      case PlaidConnectionStatus.NEEDS_REAUTH:
        return 'Needs Reconnection';
      case PlaidConnectionStatus.DISCONNECTED:
        return 'Disconnected';
      case PlaidConnectionStatus.ERROR:
        return 'Error';
      default:
        return 'Unknown';
    }
  },
  
  /**
   * Get connection status color
   */
  getConnectionStatusColor: (status: PlaidConnectionStatus): string => {
    switch (status) {
      case PlaidConnectionStatus.ACTIVE:
        return 'text-green-600';
      case PlaidConnectionStatus.NEEDS_REAUTH:
        return 'text-yellow-600';
      case PlaidConnectionStatus.DISCONNECTED:
        return 'text-gray-600';
      case PlaidConnectionStatus.ERROR:
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  },
  
  /**
   * Format last sync time
   */
  formatLastSync: (date?: Date | string): string => {
    if (!date) return 'Never';
    const d = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return d.toLocaleDateString();
  }
};